import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  MdSchool,
  MdAdd,
  MdEdit,
  MdDelete,
  MdClose,
  MdSave,
  MdWarehouse,
  MdLocalFireDepartment,
  MdBuild,
  MdOutlineOpacity,
  MdScale,
  MdMenu,
  MdFactory,
  MdScience,
  MdAssessment,
  MdRefresh,
  MdSearch,
} from "react-icons/md";
import toast from "react-hot-toast";
import { useTrainingTopics } from "context/TrainingTopicsContext";
import { getSectors } from "api/sectors";
import { createTrainingTopic, getTrainingTopics } from "api/trainingTopics";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

const ICON_MAP = {
  warehouse: MdWarehouse,
  steam: MdLocalFireDepartment,
  build: MdBuild,
  opacity: MdOutlineOpacity,
  scale: MdScale,
  factory: MdFactory,
  science: MdScience,
  assessment: MdAssessment,
};

function AddEditTopicModal({ open, onClose, onSave, topic = null, sectorName }) {
  const isEdit = Boolean(topic?.id);
  const [form, setForm] = useState({
    name: topic?.name || "",
    definition: topic?.definition || "",
    startDate: topic?.startDate || "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      if (topic) {
        setForm({
          name: topic.name || "",
          definition: topic.definition || "",
          startDate: topic.startDate || "",
        });
      } else {
        setForm({ name: "", definition: "", startDate: "" });
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, topic]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Topic name is required";
    if (!form.definition.trim()) nextErrors.definition = "Definition is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSave({
        ...(topic || {}),
        name: form.name.trim(),
        definition: form.definition.trim(),
        startDate: form.startDate || null,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="topic-modal-title"
      >
        <div className="shrink-0 flex items-center justify-between gap-4 p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 id="topic-modal-title" className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
            {isEdit ? "Edit training topic" : "Add training topic"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
            {sectorName && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Sector: <span className="font-bold text-slate-700 dark:text-slate-200">{sectorName}</span>
              </p>
            )}
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Topic name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`w-full rounded-xl border ${errors.name ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400`}
                placeholder="e.g. Safety & PPE"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Definition <span className="text-red-500">*</span>
              </label>
              <textarea
                name="definition"
                value={form.definition}
                onChange={handleChange}
                rows={3}
                className={`w-full rounded-xl border ${errors.definition ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400 resize-none`}
                placeholder="Describe what this training topic covers…"
              />
              {errors.definition && <p className="text-xs text-red-500 mt-1">{errors.definition}</p>}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Training start date
              </label>
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none"
              />
            </div>
          </div>
          <div className="shrink-0 p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl text-white font-black text-sm shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: SECONDARY, boxShadow: `${SECONDARY}33 0 10px 20px` }}
            >
              <MdSave className="text-lg" />
              {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Add topic"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function DeleteConfirmModal({ topic, onClose, onConfirm }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-6"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-topic-title"
      >
        <h2 id="delete-topic-title" className="text-lg font-black text-slate-900 dark:text-white mb-2">
          Delete training topic?
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          This will permanently remove{" "}
          <span className="font-bold text-slate-900 dark:text-white">{topic?.name ?? "this topic"}</span>.
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (topic?.id) onConfirm(topic.id);
              onClose();
            }}
            className="px-6 py-2.5 rounded-xl font-bold text-sm text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <MdDelete className="text-lg" />
            Delete
          </button>
        </div>
      </div>
    </>
  );
}

function mapApiTopicToLocal(apiTopic) {
  return {
    id: apiTopic.id,
    name: apiTopic.name,
    definition: apiTopic.definition || "",
    sector_name: apiTopic.sector_name,
    startDate: apiTopic.start_date || null,
    start_date: apiTopic.start_date,
    created_at: apiTopic.created_at,
    updated_at: apiTopic.updated_at,
  };
}

export default function TrainingTopicsManagement() {
  const { addTopic } = useTrainingTopics();
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [sectorsError, setSectorsError] = useState(null);
  const [allTopics, setAllTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState(null);
  const [activeSectorId, setActiveSectorId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sectorSearch, setSectorSearch] = useState("");
  const [sectorPage, setSectorPage] = useState(1);
  const sectorsPerPage = 5;
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [deleteTopic, setDeleteTopic] = useState(null);

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    setSectorsError(null);
    try {
      const result = await getSectors();
      const list = (result.data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        iconKey: s.icon_key || s.iconKey,
        icon: ICON_MAP[s.icon_key || s.iconKey] || MdSchool,
        created_at: s.created_at,
        updated_at: s.updated_at,
      }));
      setSectors(list);
    } catch (err) {
      setSectorsError(err.response?.data?.message || err.message || "Failed to load sectors");
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  const fetchTopics = useCallback(async () => {
    setTopicsLoading(true);
    setTopicsError(null);
    try {
      const result = await getTrainingTopics();
      const list = (result.data || []).map(mapApiTopicToLocal);
      setAllTopics(list);
    } catch (err) {
      setTopicsError(err.response?.data?.message || err.message || "Failed to load training topics");
      setAllTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (sectors.length > 0 && !activeSectorId) {
      setActiveSectorId(sectors[0].id);
    }
  }, [sectors, activeSectorId]);

  const filteredSectors = useMemo(() => {
    if (!sectorSearch.trim()) return sectors;
    const q = sectorSearch.trim().toLowerCase();
    return sectors.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [sectors, sectorSearch]);

  const totalSectorPages = Math.max(1, Math.ceil(filteredSectors.length / sectorsPerPage));
  const paginatedSectors = useMemo(() => {
    const start = (sectorPage - 1) * sectorsPerPage;
    return filteredSectors.slice(start, start + sectorsPerPage);
  }, [filteredSectors, sectorPage]);

  const activeSector = sectors.find((s) => s.id === activeSectorId) || sectors[0] || { id: null, name: "—" };
  const topics = useMemo(
    () =>
      allTopics.filter((t) => (t.sector_name || "").trim() === (activeSector?.name || "").trim()),
    [allTopics, activeSector?.name]
  );

  const getTopicCountForSector = useCallback(
    (sectorName) => allTopics.filter((t) => (t.sector_name || "").trim() === (sectorName || "").trim()).length,
    [allTopics]
  );

  const closeSidebar = () => setSidebarOpen(false);
  const selectSector = (id) => {
    setActiveSectorId(id);
    closeSidebar();
  };

  const handleOpenAdd = () => {
    setEditTopic(null);
    setAddEditOpen(true);
  };

  const handleOpenEdit = (topic) => {
    setEditTopic(topic);
    setAddEditOpen(true);
  };

  const handleSaveTopic = async (topicData) => {
    if (editTopic?.id) {
      // Local update until API support
      setAllTopics((prev) =>
        prev.map((t) =>
          t.id === editTopic.id
            ? {
                ...t,
                name: topicData.name,
                definition: topicData.definition,
                startDate: topicData.startDate || null,
              }
            : t
        )
      );
      setEditTopic(null);
      return;
    }
    try {
      const result = await createTrainingTopic({
        sector_name: activeSector?.name || "",
        name: topicData.name,
        definition: topicData.definition,
        start_date: topicData.startDate || null,
      });
      const created = result.data;
      addTopic(activeSectorId, mapApiTopicToLocal(created));
      await fetchTopics();
      toast.success(result.message || "Training topic created successfully");
      setEditTopic(null);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create topic");
      throw err;
    }
  };

  const handleDeleteConfirm = (topicId) => {
    setAllTopics((prev) => prev.filter((t) => t.id !== topicId));
    setDeleteTopic(null);
  };

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4">
        <div className="space-y-1">
          <span
            className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
            style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
          >
            Training
          </span>
          <h2 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Training topics
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-2xl">
            Create and manage training topics for each sector. Topics are used when conducting operator training.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden flex items-center gap-2 px-4 py-2.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          <MdMenu className="text-lg" />
          <span>Sectors</span>
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar overlay for mobile */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={closeSidebar} aria-hidden="true" />
        )}
        {/* Sidebar */}
        <aside
          className={`fixed top-0 left-0 z-50 h-full w-[min(100%,18rem)] max-w-[18rem] flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col overflow-hidden transition-transform duration-200 ease-out lg:transition-none
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
            lg:relative lg:translate-x-0 lg:w-72 lg:max-w-none lg:rounded-2xl lg:border lg:shadow-sm`}
        >
          <div className="shrink-0 p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Sectors</h3>
                <p className="text-xs text-slate-500 font-medium">Select to manage topics</p>
              </div>
              <button
                type="button"
                onClick={closeSidebar}
                className="lg:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                aria-label="Close menu"
              >
                <MdClose className="text-xl" />
              </button>
            </div>
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base pointer-events-none" />
              <input
                type="text"
                value={sectorSearch}
                onChange={(e) => {
                  setSectorSearch(e.target.value);
                  setSectorPage(1);
                }}
                placeholder="Search sectors…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-transparent focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5">
            {sectorsLoading ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-4">Loading sectors…</p>
            ) : sectorsError ? (
              <div className="space-y-2 py-4">
                <p className="text-sm text-red-500">{sectorsError}</p>
                <button
                  type="button"
                  onClick={fetchSectors}
                  className="flex items-center gap-2 text-sm font-bold text-[#0e76a8] hover:underline"
                >
                  <MdRefresh className="text-lg" />
                  Retry
                </button>
              </div>
            ) : (
            <nav className="flex flex-col gap-1.5">
              {paginatedSectors.map((s) => {
                const Icon = s.icon || MdSchool;
                const isActive = s.id === activeSectorId;
                const topicCount = getTopicCountForSector(s.name);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectSector(s.id)}
                    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isActive
                        ? "bg-[#f27405]/10 text-[#f27405]"
                        : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="text-xl flex-shrink-0" />
                      <span className="text-sm font-semibold truncate">{s.name}</span>
                    </div>
                    <span className="text-[10px] font-black text-slate-400 flex-shrink-0">
                      {topicCount} topics
                    </span>
                  </button>
                );
              })}
              {paginatedSectors.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-4">
                  {sectors.length === 0
                    ? "No sectors yet."
                    : "No sectors match your search."}
                </p>
              )}
            </nav>
            )}
          </div>
          {!sectorsLoading && !sectorsError && filteredSectors.length > sectorsPerPage && (
            <div className="shrink-0 px-4 sm:p-5 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                Page {sectorPage} of {totalSectorPages}
              </p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setSectorPage((p) => Math.max(1, p - 1))}
                  disabled={sectorPage <= 1}
                  className="px-2 py-1 rounded text-xs font-bold border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setSectorPage((p) => Math.min(totalSectorPages, p + 1))}
                  disabled={sectorPage >= totalSectorPages}
                  className="px-2 py-1 rounded text-xs font-bold border border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col gap-6 min-w-0">
          {/* Sector header */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-6 flex flex-wrap justify-between items-start gap-4">
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                TRAINING TOPICS FOR
              </p>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                {activeSector.name}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {topics.length} topic{topics.length !== 1 ? "s" : ""} configured
              </p>
            </div>
            <button
              type="button"
              onClick={handleOpenAdd}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-black text-white shadow-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: SECONDARY, boxShadow: `${SECONDARY}33 0 10px 20px` }}
            >
              <MdAdd className="text-lg" />
              <span>Add topic</span>
            </button>
          </div>

          {/* Topics table */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm flex flex-col">
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MdSchool className="text-xl" style={{ color: PRIMARY }} />
                <h3 className="font-black text-slate-900 dark:text-white">Topics</h3>
              </div>
              {!topicsLoading && !topicsError && (
                <button
                  type="button"
                  onClick={fetchTopics}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 transition-colors"
                  title="Refresh topics"
                  aria-label="Refresh topics"
                >
                  <MdRefresh className="text-lg" />
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              {topicsLoading ? (
                <div className="p-12 text-center">
                  <p className="text-slate-500 dark:text-slate-400 font-medium">Loading topics…</p>
                </div>
              ) : topicsError ? (
                <div className="p-12 text-center space-y-3">
                  <p className="text-sm text-red-500">{topicsError}</p>
                  <button
                    type="button"
                    onClick={fetchTopics}
                    className="flex items-center gap-2 mx-auto px-4 py-2 text-sm font-bold text-[#0e76a8] hover:underline"
                  >
                    <MdRefresh className="text-lg" />
                    Retry
                  </button>
                </div>
              ) : topics.length === 0 ? (
                <div className="p-12 text-center">
                  <MdSchool className="mx-auto text-4xl text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-slate-500 dark:text-slate-400 font-medium">
                    No training topics yet. Add one to get started.
                  </p>
                </div>
              ) : (
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Topic</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Definition</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Start date</th>
                      <th className="px-4 sm:px-6 py-3 text-right text-xs font-black uppercase text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topics.map((topic) => (
                        <tr
                          key={topic.id}
                          className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30"
                        >
                          <td className="px-4 sm:px-6 py-4">
                            <div className="flex items-center gap-2">
                              <MdSchool className="text-lg text-slate-500 shrink-0" />
                              <span className="font-bold text-slate-900 dark:text-white">{topic.name}</span>
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-slate-600 dark:text-slate-400 max-w-xs">
                            {topic.definition}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {topic.startDate
                              ? new Date(topic.startDate).toLocaleDateString("en-GB")
                              : "—"}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => handleOpenEdit(topic)}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                                title="Edit"
                                aria-label="Edit"
                              >
                                <MdEdit className="text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTopic(topic)}
                                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Delete"
                                aria-label="Delete"
                              >
                                <MdDelete className="text-lg" />
                              </button>
                            </div>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddEditTopicModal
        open={addEditOpen}
        onClose={() => {
          setAddEditOpen(false);
          setEditTopic(null);
        }}
        onSave={handleSaveTopic}
        topic={editTopic}
        sectorName={activeSector.name}
      />
      {deleteTopic && (
        <DeleteConfirmModal
          topic={deleteTopic}
          onClose={() => setDeleteTopic(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
